/**
 * messageCache.js
 * IndexedDB 기반 채팅 메시지 로컬 캐시
 * 방 입장 시 캐시된 메시지를 즉시 표시 → 서버 동기화
 */

const DB_NAME = 'ai-schedule-cache';
const DB_VERSION = 1;
const STORE_MESSAGES = 'messages';
const MAX_CACHED_PER_ROOM = 150; // 방별 최대 캐시 메시지 수 (이전 페이지 포함)

let db = null;

function openDB() {
  if (db) return Promise.resolve(db);
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const database = e.target.result;
      if (!database.objectStoreNames.contains(STORE_MESSAGES)) {
        const store = database.createObjectStore(STORE_MESSAGES, { keyPath: 'roomId' });
        store.createIndex('roomId', 'roomId', { unique: true });
      }
    };
    req.onsuccess = (e) => {
      db = e.target.result;
      db.onclose = () => { db = null; };
      db.onversionchange = () => { db.close(); db = null; };
      resolve(db);
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * 방의 캐시된 메시지 가져오기
 * @param {string} roomId
 * @returns {Promise<Array>} 메시지 배열 (없으면 [])
 */
export async function getCachedMessages(roomId) {
  try {
    const database = await openDB();
    return new Promise((resolve) => {
      try {
        const tx = database.transaction(STORE_MESSAGES, 'readonly');
        const store = tx.objectStore(STORE_MESSAGES);
        const req = store.get(roomId);
        req.onsuccess = () => resolve(req.result?.messages || []);
        req.onerror = () => resolve([]);
      } catch {
        db = null; // 연결이 닫혀 있으면 리셋 → 다음 호출 시 재연결
        resolve([]);
      }
    });
  } catch {
    return [];
  }
}

/**
 * 방의 메시지 캐시 저장 (최근 50개만 보관)
 * @param {string} roomId
 * @param {Array} messages
 */
export async function setCachedMessages(roomId, messages) {
  try {
    const database = await openDB();
    const recent = messages.slice(-MAX_CACHED_PER_ROOM);
    return new Promise((resolve) => {
      try {
        const tx = database.transaction(STORE_MESSAGES, 'readwrite');
        const store = tx.objectStore(STORE_MESSAGES);
        store.put({ roomId, messages: recent, updatedAt: Date.now() });
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
      } catch {
        db = null;
        resolve();
      }
    });
  } catch {
    // 캐시 실패해도 앱 동작에 영향 없음
  }
}

/**
 * 캐시에 새 메시지 추가 (소켓으로 수신 시)
 * @param {string} roomId
 * @param {Object} message
 */
export async function appendCachedMessage(roomId, message) {
  try {
    const cached = await getCachedMessages(roomId);
    const updated = [...cached, message].slice(-MAX_CACHED_PER_ROOM);
    await setCachedMessages(roomId, updated);
  } catch {
    // 무시
  }
}

/**
 * 캐시에서 메시지 삭제
 * @param {string} roomId
 * @param {string} messageId
 */
export async function deleteCachedMessage(roomId, messageId) {
  try {
    const cached = await getCachedMessages(roomId);
    const updated = cached.filter(m => m._id !== messageId);
    await setCachedMessages(roomId, updated);
  } catch {
    // 무시
  }
}

/**
 * 이전 메시지를 캐시 앞쪽에 추가 (loadMore 결과 캐싱)
 * @param {string} roomId
 * @param {Array} olderMessages - 더 오래된 메시지 배열
 */
export async function prependCachedMessages(roomId, olderMessages) {
  try {
    const current = await getCachedMessages(roomId);
    const combined = [...olderMessages, ...current];
    // _id 기준 중복 제거
    const seen = new Set();
    const deduped = combined.filter(m => {
      if (seen.has(m._id)) return false;
      seen.add(m._id);
      return true;
    });
    await setCachedMessages(roomId, deduped);
  } catch {
    // 무시
  }
}

/**
 * 특정 방 캐시 삭제
 * @param {string} roomId
 */
export async function clearRoomCache(roomId) {
  try {
    const database = await openDB();
    return new Promise((resolve) => {
      const tx = database.transaction(STORE_MESSAGES, 'readwrite');
      tx.objectStore(STORE_MESSAGES).delete(roomId);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    // 무시
  }
}
